import { Card } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  Area,
  AreaChart,
  Cell,
} from "recharts";

const userActivityData = [
  { name: "Jan", users: 400, applications: 240, verifications: 350 },
  { name: "Feb", users: 300, applications: 139, verifications: 280 },
  { name: "Mar", users: 200, applications: 980, verifications: 420 },
  { name: "Apr", users: 278, applications: 390, verifications: 310 },
  { name: "May", users: 189, applications: 480, verifications: 290 },
];

const verificationStats = [
  { name: "Pending", value: 20, color: "#FCD34D" },
  { name: "Verified", value: 45, color: "#34D399" },
  { name: "Rejected", value: 10, color: "#F87171" },
  { name: "On Hold", value: 15, color: "#60A5FA" },
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 md:p-4 rounded-lg shadow-lg border">
        <p className="font-semibold text-sm md:text-base">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.color }} className="text-xs md:text-sm">
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const ActivityCharts = () => {
  const isMobile = useIsMobile();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
      <Card className="p-4 md:p-6 hover:shadow-lg transition-all duration-200">
        <h2 className="text-lg md:text-xl font-semibold mb-4">User Activity Trends</h2>
        <div className="h-[250px] md:h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={userActivityData} margin={isMobile ? { top: 5, right: 10, left: -20, bottom: 0 } : { top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorApplications" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#82ca9d" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: isMobile ? 10 : 12 }}
              />
              <YAxis 
                tick={{ fontSize: isMobile ? 10 : 12 }}
                width={isMobile ? 30 : 40}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />
              <Area type="monotone" dataKey="users" stroke="#8884d8" fillOpacity={1} fill="url(#colorUsers)" />
              <Area type="monotone" dataKey="applications" stroke="#82ca9d" fillOpacity={1} fill="url(#colorApplications)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-4 md:p-6 hover:shadow-lg transition-all duration-200">
        <h2 className="text-lg md:text-xl font-semibold mb-4">Verification Distribution</h2>
        <div className="h-[250px] md:h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={verificationStats} margin={isMobile ? { top: 5, right: 10, left: -20, bottom: 0 } : { top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: isMobile ? 10 : 12 }}
              />
              <YAxis 
                tick={{ fontSize: isMobile ? 10 : 12 }}
                width={isMobile ? 30 : 40}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value">
                {verificationStats.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
};

export default ActivityCharts;